import type { FormatXMLElementFn, Options } from 'intl-messageformat';
import type { MessageDescriptor, PrimitiveType } from 'react-intl';
import { createIntl, createIntlCache } from 'react-intl';

import i18nConfig from '../../../i18n/locales/i18n.config.json';
import fallbackTranslations from '../../../i18n/locales/en-US.json';

// Type helper for window with AvaApp (for TestCafe compatibility)
type WindowWithAvaApp = Window & {
  AvaApp?: {
    getLanguageFile?: (projectName: string, localeCode: string, version: string) => Promise<Record<string, string>>;
  };
  AvaAppConfig?: {
    env?: string;
  };
};

export type TranslationKey = keyof typeof fallbackTranslations;

const defaultLocale = 'en-US';
const cache = createIntlCache();

// Cache translation JSONs to avoid repeated loads
const translationsCache = new Map<string, { data: Record<string, string>; expires: number }>();
const cacheTimeout = 24 * 60 * 60 * 1000; // 24 hours
// Track pending requests to prevent concurrent fetches for the same locale/version
const pendingRequests = new Map<string, Promise<Record<string, string>>>();

const getI18nConfig = () => {
  return i18nConfig || {
    languageFileVersion: '',
    targetLanguages: [],
    sourceLanguage: 'en_US',
    projectName: 'buy',
  };
};

export const getBaseAssetsUrl = (): string => {
  const win = typeof window !== 'undefined' ? (window as WindowWithAvaApp) : undefined;
  const env = process.env.NODE_ENV || win?.AvaAppConfig?.env;
  if (env === 'local' || env === 'ci' || env === 'qa' || env === 'test' || env === 'development') {
    return 'https://assets.avalara.io';
  }
  return 'https://assets.avalara.com';
};

export const getLanguageFileVersion = (): string => {
  const config = getI18nConfig();
  return config.languageFileVersion || '';
};

export const getSupportedLocales = (): string[] => {
  const config = getI18nConfig();
  const { targetLanguages = [], sourceLanguage = 'en_US' } = config;

  return [
    sourceLanguage.replace('_', '-'), // en_US -> en-US
    ...targetLanguages.map((lang: string) => lang.replace('_', '-')),
  ];
};

export const toCamelCase = (str: string): string =>
  str
    .toLowerCase()
    .replace(/[\s\-_]+(.)/g, (_, group) => group.toUpperCase())
    .replace(/^[A-Z]/, (match) => match.toLowerCase());

const buildCacheKey = (locale: string, languageVersion?: string): string => {
  const version = languageVersion || getLanguageFileVersion();
  return `i18n:${locale}:${version}`;
};

const fetchLocalFallback = (): Record<string, string> => fallbackTranslations;

export const fetchTranslation = async (locale: string): Promise<Record<string, string>> => {
  const config = getI18nConfig();
  const projectName = config.projectName || 'buy';
  const languageVersion = getLanguageFileVersion();
  const cacheKey = buildCacheKey(locale, languageVersion);

  // Match earlier behavior: en-US always comes from local fallback (guaranteed present + best for SSR)
  if (locale === defaultLocale) return fetchLocalFallback();

  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey)!;
  }

  const cached = translationsCache.get(cacheKey);
  if (cached && Date.now() < cached.expires) {
    return cached.data;
  }

  const fetchPromise = (async (): Promise<Record<string, string>> => {
    // Preferred for buy-dot: AvaApp API (already wired in One Web Domain runtime).
    const win = typeof window !== 'undefined' ? (window as WindowWithAvaApp) : undefined;
    if (win?.AvaApp?.getLanguageFile) {
      try {
        const translations = await win.AvaApp.getLanguageFile(projectName, locale, languageVersion);
        if (translations && Object.keys(translations).length > 0) {
          translationsCache.set(cacheKey, { data: translations, expires: Date.now() + cacheTimeout });
          return translations;
        }
      } catch {
        // fall through to default fallback behavior
      }
    }

    // If non-default locale fails, fall back to en-US.
    return fetchLocalFallback();
  })();

  pendingRequests.set(cacheKey, fetchPromise);
  try {
    return await fetchPromise;
  } finally {
    pendingRequests.delete(cacheKey);
  }
};

export const clearCache = (locale?: string): void => {
  if (locale) {
    const key = buildCacheKey(locale);
    translationsCache.delete(key);
    pendingRequests.delete(key);
  } else {
    translationsCache.clear();
    pendingRequests.clear();
  }
};

// Create initial intl instance with fallback translations so `t()` works immediately.
let intlInstance = createIntl(
  {
    locale: defaultLocale,
    messages: fetchLocalFallback(),
    defaultLocale,
  },
  cache,
);

export const getIntl = () => intlInstance;

// Backwards-compatible: allow imperative updating (used by IntlProviderWrapper historically)
export const updateIntl = (messages: Record<string, string>, locale = defaultLocale): void => {
  const mergedMessages =
    locale === defaultLocale
      ? messages
      : {
          ...fetchLocalFallback(),
          ...messages,
        };

  intlInstance = createIntl(
    {
      locale,
      messages: mergedMessages,
      defaultLocale,
    },
    cache,
  );
};

export const createIntlInstance = async (locale: string) => {
  const supportedLocales = getSupportedLocales();
  const validLocale = supportedLocales.includes(locale) ? locale : defaultLocale;
  const messages = await fetchTranslation(validLocale);
  updateIntl(messages, validLocale);
  return getIntl();
};

export function translate(id: TranslationKey, values?: Record<string, PrimitiveType | FormatXMLElementFn<string, string>>, opts?: Options): string;
export function translate(
  id: TranslationKey,
  values?: Record<string, React.ReactNode | PrimitiveType | FormatXMLElementFn<string, React.ReactNode>>,
  opts?: Options,
): Array<React.ReactNode>;
export function translate(id: TranslationKey, values?: Record<string, unknown>): unknown {
  // `formatMessage` returns string for primitive-only values, and an array of nodes when rich values are used.
  return intlInstance.formatMessage({ id } as MessageDescriptor, values as any);
}

export function t(key: TranslationKey, values?: Record<string, PrimitiveType>): string {
  return intlInstance.formatMessage({ id: key }, values);
}


